import {CallJs} from "../types";
import * as Url from 'url'
import {FileProgress} from "./fileprogress";
import {makeAcceptFromExtensions, matchesGlob} from "../extensions";
import * as handler from "./handler"

export type UploadCallback = (this: SWFUploaderInstance, file: SWFUploadFile, serverData: string, response: string) => void
export type UploadCompleteCallback = (this: SWFUploaderInstance, f: SWFUploadFile) => void
export type QueueCompleteCallback = (this: SWFUploaderInstance) => void

type SWFUploaderMethods = {
	getStats: () => { files_queued: number }
	cancelUpload: (fileId: string) => void
	cancelQueue: () => void
	updateUploadUrl: (url:string) => void
}

export type SWFUploaderInstance = SWFUploaderMethods & SWFUploadParams

export interface SWFUploadParams {
	file_size_limit: string
	file_types: {}
	file_types_description: {}
	file_upload_limit: string
	file_queue_limit: {}
	custom_settings: {progressTarget: string, cancelButtonId: string}
	button_width: string
	button_height: string
	button_placeholder_id: string
	file_queued_handler: typeof fileQueued
	file_queue_error_handler: typeof fileQueueError
	upload_start_handler: typeof uploadStart
	upload_progress_handler: typeof uploadProgress
	upload_error_handler: typeof uploadError
	upload_success_handler: UploadCallback
	upload_complete_handler: UploadCompleteCallback
	queue_complete_handler: (num_uploaded_files: number) => void
}

export interface SWFUploadFile {
	id: string
	name: string
}

 interface SwfUploaderTypescriptParams {
	showCancelButton: boolean
	showQueue: boolean
	showUploadTotal: boolean
	uploadUrl: string
	maxSize: string
	fileTypes: string
	fileTypesDescription: string
	queueLimit: number
	uploadCallback: UploadCallback
	uploadComplete?: UploadCompleteCallback
	queueComplete?: QueueCompleteCallback
	showUploadedTotal: boolean
	id?: string
}

 interface SwfUploaderCallJsParams {
	showCancelButton: boolean
	showQueue: boolean
	showUploadTotal: boolean
	uploadUrl: string
	maxSize: string
	fileTypes: string
	fileTypesDescription: string
	queueLimit: number
	uploadCallback: string
	uploadComplete: string
	queueComplete: string
	showUploadedTotal: boolean
	id?: string
}

const swfUploaderFile = (file: File, id: number) => ({
	id: id.toString(),
	name: file.name
})

//it seems there's some sort of global state in this. it isn't worth removing it; we should remove swfuploader
//still, till then, here's our static counter:
let _uploaderCount = 1

interface SwfUploaders {
	[name: string]: SWFUploaderInstance
}

declare var window: {ivt?: {sbhtml?: {swfUploaders?: SwfUploaders}}}

type XHRPromiseFulfilled = {xhr: XMLHttpRequest, event: Event}

/** @returns {Promise<XHRPromiseFulfilled>} It doesn't care why it finished. It merely promises that it finished. */
const xhrToPromise = (xhr: XMLHttpRequest, go: (xhr: XMLHttpRequest) => void): Promise<XHRPromiseFulfilled> => {
	const promise = new Promise<XHRPromiseFulfilled>(resolve => {
		xhr.onreadystatechange = (event) => {
			if (xhr.readyState == 4)
				resolve({xhr, event})
		}
	})
	go(xhr)
	return promise
}

type FileStatus = {state: "pending"} | {state: "uploading", xhr: XMLHttpRequest} | {state: "done (whether successfully or not)"}
type FileState = FileStatus & {
	file: File,
	swfFile: SWFUploadFile,
	index: string
}

const cancelUpload = (f: FileState): FileState => {
	switch (f.state) {
		case "uploading":
			f.xhr.abort()
	}

	return {file: f.file, swfFile: f.swfFile, index: f.index, state: "done (whether successfully or not)"}
}

const yieldNextPending = (set: FileState[]): FileState | undefined =>
	set.find(s => s.state == "pending")

/**
 * in order to use this method, the php still needs to make sure the header javascript
 * is added, because i really don't have the time to deal with that for code that is
 * probably going to be thrown away in a few months anyway.
 *
 * @see \SwfUploadWrapper->render() the top thereof.
 */
export const swfuploader: CallJs<SwfUploaderTypescriptParams> = (replace, params) => {
	if (!handler.done) {
		return
	}

	const uploaderCount = String(_uploaderCount++);

	window.ivt = window.ivt || {};
	window.ivt.sbhtml = window.ivt.sbhtml || {}
	window.ivt.sbhtml.swfUploaders = window.ivt.sbhtml.swfUploaders || {}

	const node = document.createElement('div');
	replace(node);

	const outerDiv = document.createElement('div');
	outerDiv.className = 'swfUploadOuterDiv';
	node.appendChild(outerDiv);

	const fileUpload = document.createElement('input');
	fileUpload.multiple = params.queueLimit != 1
	fileUpload.type = "file"
	const filetypes = makeAcceptFromExtensions(params.fileTypes);
	fileUpload.accept = filetypes.join(', ')
	outerDiv.appendChild(fileUpload);

	const cancelButton = document.createElement('button');
	cancelButton.id = 'btnCancel' + uploaderCount;
	cancelButton.type = 'button';
	cancelButton.className = 'swfUploadCancel';
	cancelButton.style.display = params.showCancelButton ? 'block' : 'none';
	cancelButton.innerHTML = 'Cancel All Uploads';
	cancelButton.disabled = true;
	outerDiv.appendChild(cancelButton);

	const swfUploaderDiv = document.createElement('div');
	swfUploaderDiv.id = 'fsUploadProgress' + uploaderCount;
	swfUploaderDiv.className = 'swfUploadFieldset swfUploadflash';
	node.appendChild(swfUploaderDiv);

	if (params.showQueue) {
		const progressSpan = document.createElement('span');
		progressSpan.id = 'swfUploadProgressLegend' + uploaderCount;
		progressSpan.innerHTML = 'Upload Queue';
		swfUploaderDiv.appendChild(progressSpan);
	}

	let statusDiv: HTMLDivElement|null = null;
	if (params.showUploadTotal) {
		statusDiv = document.createElement('div');
		statusDiv.innerHTML = '0 Files Uploaded';
		node.appendChild(statusDiv);
	}

	let cancelled = false

	const swfUploader: SWFUploaderInstance = {
		file_size_limit : params.maxSize + " MB",
		file_types : params.fileTypes,
		file_types_description : params.fileTypesDescription,
		file_upload_limit : params.maxSize,
		file_queue_limit : params.queueLimit,
		custom_settings :
		{
			progressTarget : swfUploaderDiv.id,
			cancelButtonId : cancelButton.id
		},

		button_width: "61",
		button_height: "22",
		button_placeholder_id: fileUpload.id,

		// The event handler functions are defined in handlers.js
		file_queued_handler : fileQueued,
		file_queue_error_handler : fileQueueError,
		upload_start_handler : uploadStart,
		upload_progress_handler : uploadProgress,
		upload_error_handler : uploadError,

		getStats: () => ({files_queued: fileSet.filter(s => s.state == "pending").length}),
		cancelUpload: (fileId) => fileSet.map(f => f.index == fileId ? cancelUpload(f) : f),
		cancelQueue: () => fileSet.map(cancelUpload),
		updateUploadUrl: (url) => { params.uploadUrl = url },

		upload_success_handler: function (file: SWFUploadFile, serverData: string, response: string) {
			const progress = new FileProgress(file, swfUploaderDiv.id);
			progress.setComplete();
			progress.setStatus("Complete.");
			progress.toggleCancel(false);

			if(params.uploadCallback) {
				params.uploadCallback.call(this, file, serverData, response);
			}
		},

		upload_complete_handler: function(File: SWFUploadFile) {
			cancelButton.disabled = true;
			if (params.uploadComplete)
				params.uploadComplete.call(this, File)
		},

		queue_complete_handler : function(numFilesUploaded: number) {
			if (params.showUploadedTotal && statusDiv != null)
				statusDiv.innerHTML = numFilesUploaded + " file" + (numFilesUploaded === 1 ? "" : "s") + " uploaded.";

			if (params.queueComplete)
				params.queueComplete.call(this)
		}
	}

	let fileIdCount = 0;
	const fileSet: FileState[] = []

	if(params.id) {
		window.ivt.sbhtml.swfUploaders[params.id] = swfUploader
	}

	fileUpload.onchange = () => {
		if(fileUpload && fileUpload.files) {
			// first, we validate
			// `fileUpload.files` is a FileList, not an Array. consequently we manually iterate. But i want some isolation, so i do a async iife
			for (let i = 0; i < fileUpload.files.length; i++) {
				const file = fileUpload.files[i]
				const swfFile = swfUploaderFile(file, fileIdCount++)

				const maxSize = Number.parseFloat(params.maxSize)
				if (maxSize && file.size > (maxSize * 1000000)) {
					swfUploader.file_queue_error_handler(swfFile, SWFUpload.QUEUE_ERROR.FILE_EXCEEDS_SIZE_LIMIT, `The file ${file.name} cannot be uploaded because it is too large. Files must be less than  ${maxSize} MB, but this file is ${file.size / 1000000} MB`)
				} else if (!matchesGlob(file.name, params.fileTypes)) {
					swfUploader.file_queue_error_handler(swfFile, SWFUpload.QUEUE_ERROR.INVALID_FILETYPE, `The file ${file.name} cannot be uploaded because it is of type ${file.type}. Please select a file of type ${filetypes.join(', ')}`)
				}
				swfUploader.file_queued_handler(swfFile)
				fileSet.push({file, swfFile, index: `UploadFileDataEle${fileIdCount}`, state: "pending"})
			}

			// then we upload.
			;(async () => {
				cancelButton.disabled = false
				const go = (fd: FormData) => (xhr: XMLHttpRequest) => {
					xhr.open('POST', params.uploadUrl)
					xhr.send(fd)
				}

				for (let fileState = yieldNextPending(fileSet); fileState; fileState = yieldNextPending(fileSet)) {
					const xhr = new XMLHttpRequest()

					const file = fileState.file
					const fd = new FormData()

					const filename = file.name
					fd.append("Filedata", file)

					const r = await xhrToPromise(xhr, go(fd))
					swfUploader.upload_start_handler(fileState.swfFile)
					cancelButton.onclick = () => { cancelled = true; xhr.abort() }
					fileState.state = "done (whether successfully or not)"

					if (r.xhr.status == 200) {
						swfUploader.upload_complete_handler(fileState.swfFile)
						swfUploader.upload_success_handler(fileState.swfFile, (/* todo me*/r.event.target as any).responseText, '')

						continue
					} else if (r.xhr.status <= 0) {
						swfUploader.upload_error_handler(
							fileState.swfFile,
							SWFUpload.UPLOAD_ERROR.FILE_CANCELLED,
							`An error occurred uploading file no. ${fileState.index}, ${filename}. No further files have been uploaded.`)
					} else {
						swfUploader.upload_error_handler(
							fileState.swfFile,
							SWFUpload.UPLOAD_ERROR.HTTP_ERROR,
							`An error occurred uploading file no. ${fileState.index}, ${filename}. No further files have been uploaded.`)
					}

					cancelButton.onclick = () => {}
					cancelButton.disabled = true

					return /* there's a continue above. the code below is not redundant*/
				}

				swfUploader.queue_complete_handler(fileSet.length)
				fileUpload.value = ''
			} )()
		}

		const file = fileUpload && fileUpload.files ? fileUpload.files[0] : null;
		if (!file) return
	}

	cancelButton.onclick = () => swfUploader.cancelQueue()
}

 export const swfuploadercalljs = (replace: (div: Node) => void, params: SwfUploaderCallJsParams) => {
	const ts_params: SwfUploaderTypescriptParams = {
		showCancelButton: params.showCancelButton,
		showQueue: params.showQueue,
		showUploadTotal: params.showUploadTotal,
		uploadUrl: params.uploadUrl,
		maxSize: "1", //params.maxSize,
		fileTypes: params.fileTypes,
		fileTypesDescription: params.fileTypesDescription,
		queueLimit: params.queueLimit,
		uploadCallback: new Function("file", "serverData", "response", params.uploadCallback) as UploadCallback,
		uploadComplete: new Function("file", params.uploadComplete) as UploadCompleteCallback,
		queueComplete: new Function(params.queueComplete) as () => void,
		showUploadedTotal: params.showUploadedTotal,
		id: params.id
	}

	swfuploader(replace, ts_params)
}

export const SWFUpload = {
	QUEUE_ERROR: {
		QUEUE_LIMIT_EXCEEDED: -100,
		FILE_EXCEEDS_SIZE_LIMIT: -110,
		ZERO_BYTE_FILE: -120,
		INVALID_FILETYPE: -130
	},
	UPLOAD_ERROR: {
		HTTP_ERROR: -200,
		MISSING_UPLOAD_URL: -210,
		IO_ERROR: -220,
		SECURITY_ERROR: -230,
		UPLOAD_LIMIT_EXCEEDED: -240,
		UPLOAD_FAILED: -250,
		SPECIFIED_FILE_ID_NOT_FOUND: -260,
		FILE_VALIDATION_FAILED: -270,
		FILE_CANCELLED: -280,
		UPLOAD_STOPPED: -290
	},
	FILE_STATUS: {
		QUEUED: -1,
		IN_PROGRESS: -2,
		ERROR: -3,
		COMPLETE: -4,
		CANCELLED: -5
	}
}


declare var fileQueued: FileQueuedType
declare var fileQueueError: FileQueueErrorType
declare var uploadStart: UploadStartType
declare var uploadProgress: UploadProgressType
declare var uploadError: UploadErrorType

export type UploadErrorType = (this: SWFUploaderInstance, file: SWFUploadFile, errorCode: number, message: string) => void
export type FileQueueErrorType = (this: SWFUploadParams, file: SWFUploadFile, errorCode: number|string, message: string|number) => void
export type FileQueuedType = (this: SWFUploaderInstance, file: SWFUploadFile) => void
export type UploadStartType = (this: SWFUploaderInstance, file: SWFUploadFile) => void
export type UploadProgressType = (this: SWFUploadParams, file: SWFUploadFile, bytesLoaded: number, bytesTotal: number) => void



// WEBPACK FOOTER //
// ./src/lib/swfuploader/swfuploader.ts